import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'cranes' | 'equipment' | 'projects'>('all');

  const categories = [
    { id: 'all', name: 'جميع الصور', icon: 'ri-grid-fill' },
    { id: 'cranes', name: 'الرافعات', icon: 'ri-truck-fill' },
    { id: 'equipment', name: 'المعدات', icon: 'ri-tools-fill' },
    { id: 'projects', name: 'المشاريع', icon: 'ri-building-fill' },
  ];

  const images = [
    {
      id: 1,
      category: 'cranes',
      title: 'رافعة هيدروليكية 300 طن',
      description: 'رافعة ضخمة في موقع مشروع كبير',
      image: 'massive yellow hydraulic crane 300 ton capacity at mega construction site in Saudi Arabia, giant industrial crane lifting heavy steel beams, professional construction photography',
    },
    {
      id: 2,
      category: 'cranes',
      title: 'رافعة متوسطة 130 طن',
      description: 'رافعة متوسطة في مشروع تجاري',
      image: 'medium yellow hydraulic crane 130 ton capacity working at commercial building site in Saudi Arabia, versatile mobile crane, professional photography',
    },
    {
      id: 3,
      category: 'cranes',
      title: 'رافعة خفيفة 19 طن',
      description: 'رافعة خفيفة في موقع سكني',
      image: 'small yellow hydraulic crane 19 ton capacity at residential construction site in Saudi Arabia, compact mobile crane, professional equipment photography',
    },
    {
      id: 4,
      category: 'equipment',
      title: 'شيول ثقيل',
      description: 'شيول يعمل في موقع حفر',
      image: 'heavy duty yellow wheel loader construction equipment working at excavation site in Saudi Arabia, powerful machinery moving earth, professional photography',
    },
    {
      id: 5,
      category: 'equipment',
      title: 'حفار ضخم',
      description: 'حفار في مشروع بنية تحتية',
      image: 'massive yellow excavator heavy construction equipment digging at infrastructure project in Saudi Arabia, powerful machinery, professional photography',
    },
    {
      id: 6,
      category: 'equipment',
      title: 'بوكلين متعدد الاستخدامات',
      description: 'بوكلين في موقع عمل',
      image: 'yellow backhoe loader construction equipment working at construction site in Saudi Arabia, versatile machinery, professional photography',
    },
    {
      id: 7,
      category: 'projects',
      title: 'برج سكني فاخر',
      description: 'مشروع برج سكني قيد الإنشاء',
      image: 'luxury residential tower under construction in Saudi Arabia with yellow cranes, modern high-rise building project, professional construction photography',
    },
    {
      id: 8,
      category: 'projects',
      title: 'مجمع تجاري ضخم',
      description: 'مشروع مجمع تجاري كبير',
      image: 'massive commercial complex construction project in Saudi Arabia with multiple yellow cranes, large scale development, professional photography',
    },
    {
      id: 9,
      category: 'projects',
      title: 'مشروع بنية تحتية',
      description: 'مشروع طرق وجسور',
      image: 'infrastructure construction project roads and bridges in Saudi Arabia with heavy equipment and cranes, large scale development, professional photography',
    },
    {
      id: 10,
      category: 'cranes',
      title: 'أسطول الرافعات',
      description: 'مجموعة من رافعاتنا',
      image: 'fleet of yellow hydraulic cranes parked at equipment yard in Saudi Arabia, variety of crane sizes, professional crane rental company, aerial view photography',
    },
    {
      id: 11,
      category: 'equipment',
      title: 'معدات متنوعة',
      description: 'مجموعة من المعدات الثقيلة',
      image: 'variety of yellow heavy construction equipment at work site in Saudi Arabia, loaders excavators and machinery, professional equipment photography',
    },
    {
      id: 12,
      category: 'projects',
      title: 'مشروع صناعي',
      description: 'مشروع منشأة صناعية كبرى',
      image: 'large industrial facility construction project in Saudi Arabia with cranes and heavy equipment, massive steel structure, professional photography',
    },
  ];

  const filteredImages = activeFilter === 'all' 
    ? images 
    : images.filter(img => img.category === activeFilter);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/92890686b77b0fe5629467981513e096.jpg"
            alt="معرض الصور"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-primary/75"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-20 h-20 flex items-center justify-center bg-accent rounded-full mx-auto mb-6">
              <i className="ri-image-fill text-5xl text-primary"></i>
            </div>
            <h1 className="text-6xl md:text-7xl font-black text-white mb-6">معرض الصور</h1>
            <p className="text-2xl text-accent font-bold">شاهد أحدث رافعاتنا ومعداتنا ومشاريعنا المنجزة</p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredImages.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={() => setSelectedImage(`https://readdy.ai/api/search-image?query=$%7Bimage.image%7D&width=1200&height=900&seq=gallery-${image.id}&orientation=landscape`)}
              >
                <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                  <div className="relative h-80">
                    <img
                      src={`https://readdy.ai/api/search-image?query=$%7Bimage.image%7D&width=600&height=500&seq=gallery-${image.id}&orientation=squarish`}
                      alt={image.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  
                  <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-2xl font-black text-white mb-2">{image.title}</h3>
                    <p className="text-accent font-semibold">{image.description}</p>
                  </div>

                  <div className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-zoom-in-line text-2xl text-primary"></i>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-6"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-6 left-6 w-14 h-14 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-all"
              onClick={() => setSelectedImage(null)}
            >
              <i className="ri-close-line text-3xl text-white"></i>
            </button>

            <motion.img
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              src={selectedImage}
              alt="صورة مكبرة"
              className="max-w-full max-h-full object-contain rounded-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default GalleryPage;